import Head from 'next/head'

/**
 * Warm the connection to whichever origin serves an embedded lab chart.
 *
 * Both chart blocks embed a THIRD origin — the internal Next app for `nc-audio-charts-next`,
 * the ClearBuy Laravel app for `nc-audio-chart` — and neither is requested until the reader
 * scrolls the embed into view. Without a hint the browser first learns that origin exists at
 * that moment and pays a full DNS + TCP + TLS handshake before the first byte (measured at
 * 0.5-0.7s), on top of an embed document that is already slow to answer.
 *
 * `dns-prefetch` is the load-bearing half: the resolver answer is cached for minutes, so it
 * still pays off for an embed reached long after the page loaded. `preconnect` additionally
 * opens the socket, which browsers drop after ~10s idle — worth having because the first
 * chart on a review sits close enough to the fold to be reached inside that window, and
 * harmless once it lapses (the DNS half survives either way).
 *
 * No `crossOrigin`: these connections carry a document navigation (iframe/object), not a CORS
 * fetch, and a `crossorigin` preconnect warms the anonymous pool the document load won't use.
 *
 * The `key`s are what make this safe to render per chart block — next/head collapses keyed
 * tags, so a post with nine charts still emits one pair of links.
 */
const ChartOriginHints: React.VFC<{ url: string | undefined }> = ({ url }) => {
  const origin = originOf(url)

  if (!origin) {
    return null
  }

  return (
    <Head>
      <link key={`dns-prefetch-${origin}`} rel="dns-prefetch" href={origin} />
      <link key={`preconnect-${origin}`} rel="preconnect" href={origin} />
    </Head>
  )
}

export default ChartOriginHints

/** Origin of an embed URL, or undefined when it isn't a usable absolute URL. The chart URLs
 *  come from WordPress, so a malformed one must degrade to "no hint", never to a throw. */
function originOf(url: string | undefined): string | undefined {
  if (!url) {
    return undefined
  }
  try {
    return new URL(url).origin
  } catch {
    return undefined
  }
}
